import { ChevronDownIcon, ChevronUpIcon, CursorArrowRaysIcon, TrashIcon } from '@heroicons/react/24/outline'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { ActionModal } from '@ui/components/core'
import { showToast } from '@ui/components/elements/ToastManager'
import { Instance } from '@ui/interfaces'
import axios from '@ui/lib/api/axiosHelper'
import { useAuth } from '@ui/providers/AuthProvider'
import { useInstanceStore } from '@ui/stores'
import dayjs from 'dayjs'

type Stack = {
  uuid: string
  state: 'idle' | 'active' | 'decommissioned'
  gitref: string
  version: string
  last_heartbeat: string
}

export const InstanceComponent = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const { currentOrganization } = auth
  const orgId = currentOrganization!.id

  const [instances, setInstances] = useState<Instance[]>([])
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false)
  const [selectedInstance, setSelectedInstance] = useState<Instance | null>(null)
  const [expandedInstances, setExpandedInstances] = useState<{ [key: number]: boolean }>({})
  const [stacks, setStacks] = useState<{ [key: number]: Stack[] }>({})

  const fetchInstances = useCallback(async () => {
    try {
      const response = await axios.get(`/v1/instances`)
      setInstances(response.data)
    } catch (error) {
      console.error('Error fetching instances:', error)
      showToast.error('Failed to retrieve instances')
    }
  }, [])

  useEffect(() => {
    fetchInstances()
  }, [fetchInstances])

  const { setSelectedInstanceKey } = useInstanceStore(
    useShallow((state) => ({
      setSelectedInstanceKey: state.setSelectedInstanceKey,
    }))
  )

  const removeInstance = (instanceId: number) => {
    const configuration = {
      url: `/v1/instances/${instanceId}`,
    }
    axios
      .delete(configuration.url)
      .then(() => {
        showToast.success('Instance has been removed')
        fetchInstances()
      })
      .catch((err: unknown) => {
        console.log('Failed to delete instance ', instanceId, ' from org ', orgId, ': ', err)
        showToast.error('Failed to remove instance')
      })
  }

  const fetchStacks = async (instanceId: number) => {
    try {
      const response = await axios.get(`/v1/instances/${instanceId}/stacks`)
      setStacks((prev) => ({ ...prev, [instanceId]: response.data }))
    } catch (error) {
      console.error('Error fetching stacks:', error)
    }
  }

  const updateStackState = async (instanceId: number, stackUuid: string, newState: Stack['state']) => {
    try {
      await axios.patch(`/v1/instances/${instanceId}/stacks/${stackUuid}`, { state: newState })
      await fetchStacks(instanceId)
      showToast.success('Stack state has been updated')
    } catch (error) {
      console.error('Error updating stack state:', error)
      showToast.error('Failed to update stack state')
    }
  }

  const selectInstance = (instance: Instance) => {
    setSelectedInstanceKey(instance.connect_key)
    navigate(`/instances/${instance.connect_key}/explore/events`)
  }

  const toggleInstanceExpansion = async (instanceId: number) => {
    setExpandedInstances((prev) => ({ ...prev, [instanceId]: !prev[instanceId] }))
    if (!stacks[instanceId]) {
      await fetchStacks(instanceId)
    }
  }

  return (
    <>
      <ActionModal
        title="Delete Instance"
        message="Are you sure you want to delete this instance?"
        handleAction={() => {
          removeInstance(selectedInstance?.id || 0)
          setDeleteConfirmationModal(false)
        }}
        actionIdentifier="Delete"
        open={deleteConfirmationModal}
        dismissAction={() => {
          setDeleteConfirmationModal(false)
        }}
        dismissIdentifier="Cancel"
      />

      <div className="mt-5 flow-root h-[calc(100vh-15rem)]">
        <h2 className="mb-4 text-2xl font-bold">Admin Instances Interface</h2>
        <div className="h-full w-full overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="shadow ring-1 ring-black/5 sm:rounded-lg">
              <table className="min-w-full table-fixed divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-1/6 p-3.5 text-left text-sm font-medium text-gray-900">Name</th>
                    <th className="w-1/6 p-3.5 text-left text-sm font-medium text-gray-900">Organization</th>
                    <th className="w-1/6 p-3.5 text-left text-sm font-medium text-gray-900">Number of Stacks</th>
                    <th className="w-1/6 p-3.5 text-left text-sm font-medium text-gray-900">Last online</th>
                    <th className="w-1/4 p-3.5 text-left text-sm font-medium text-gray-900">Description</th>
                    <th className="w-1/6 p-3.5 text-left text-sm font-medium text-gray-900">Connect Key</th>
                    <th className="w-12 p-3.5 text-left text-sm font-semibold text-gray-900">
                      <span className="sr-only">Remove</span>
                    </th>
                    <th className="w-12 p-3.5 text-left text-sm font-semibold text-gray-900">
                      <span className="sr-only">Show Details</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {instances.map((instance) => (
                    <React.Fragment key={instance.id}>
                      <tr className="cursor-pointer" onClick={() => toggleInstanceExpansion(instance.id)}>
                        <td className="truncate p-3.5 text-sm font-normal text-gray-900">{instance.name}</td>
                        <td className="truncate p-3.5 text-sm font-normal text-gray-900">
                          {(instance.organization && instance.organization.name) || <i>No organization</i>}
                        </td>
                        <td className="truncate p-3.5 text-sm font-normal text-gray-900">
                          {instance.stacks.length || 0}
                        </td>
                        <td className="truncate p-3.5 text-sm font-medium text-gray-900">
                          {instance.last_heartbeat ? dayjs(instance.last_heartbeat).fromNow() : 'Never'}
                        </td>
                        <td className="truncate p-3.5 text-sm font-normal text-gray-900">{instance.description}</td>
                        <td className="truncate p-3.5 text-sm font-normal text-gray-900">{instance.connect_key}</td>
                        <td className="p-3.5 text-sm font-medium">
                          <a onClick={() => selectInstance(instance)} title="Select Instance">
                            <CursorArrowRaysIcon
                              className="h-6 w-6 cursor-pointer stroke-gray-900"
                              aria-hidden="true"
                            />
                          </a>
                        </td>
                        <td className="p-3.5 text-sm font-medium">
                          <a
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedInstance(instance)
                              setDeleteConfirmationModal(true)
                            }}
                            title="Delete Instance">
                            <TrashIcon className="h-6 w-6 cursor-pointer stroke-red-800" aria-hidden="true" />
                          </a>
                        </td>
                        <td className="p-3.5 text-sm font-normal text-gray-900">
                          {expandedInstances[instance.id] ? (
                            <ChevronUpIcon className="h-5 w-5" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5" />
                          )}
                        </td>
                      </tr>
                      {expandedInstances[instance.id] && (
                        <tr>
                          <td colSpan={8}>
                            <div className="px-4 py-2">
                              <h4 className="mb-2 text-lg font-semibold">Stacks</h4>
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="p-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      UUID
                                    </th>
                                    <th className="p-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      State
                                    </th>
                                    <th className="p-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      Action
                                    </th>
                                    <th className="p-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      Git Ref
                                    </th>
                                    <th className="p-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      Version
                                    </th>
                                    <th className="p-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      Last Heartbeat
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                  {stacks[instance.id]?.map((stack) => (
                                    <tr key={stack.uuid} className={stack.state === 'active' ? 'bg-green-100' : ''}>
                                      <td className="whitespace-nowrap p-2 text-sm text-gray-500">{stack.uuid}</td>
                                      <td className="whitespace-nowrap p-2 text-sm text-gray-500">{stack.state}</td>
                                      <td className="whitespace-nowrap p-2 text-sm text-gray-500">
                                        {stack.state === 'active' ? (
                                          <span className="font-semibold text-green-600">-</span>
                                        ) : stack.state === 'idle' ? (
                                          <button
                                            onClick={() => updateStackState(instance.id, stack.uuid, 'active')}
                                            className="rounded bg-blue-500 px-2 py-1 text-white hover:bg-blue-600">
                                            Activate
                                          </button>
                                        ) : stack.state === 'decommissioned' ? (
                                          <button
                                            onClick={() => updateStackState(instance.id, stack.uuid, 'active')}
                                            className="rounded bg-green-500 px-2 py-1 text-white hover:bg-green-600">
                                            Reactivate
                                          </button>
                                        ) : (
                                          stack.state
                                        )}
                                      </td>

                                      <td className="whitespace-nowrap p-2 text-sm text-gray-500">{stack.gitref}</td>
                                      <td className="whitespace-nowrap p-2 text-sm text-gray-500">{stack.version}</td>
                                      <td className="whitespace-nowrap p-2 text-sm text-gray-500">
                                        {stack.last_heartbeat}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
